import { ModuleContainer } from '../ModuleContainer';
import styles from './M12Hero5050Module.module.scss';
import { Link, Button } from '~/shared/components';
import { ButtonColorProps, M12Hero5050Module } from '~/lib/data-contract/auto-generated/api-model';
import Typography, { TypographyStyle } from '~/shared/components/Typography/Typography';
import { Image } from '~/shared/components';
import { WaterMarkLogo, WaterMarkPercent } from '~/shared/components/WaterMark/WaterMarks';
import clsx from 'clsx';
import { useMemo } from 'react';
import dynamic from 'next/dynamic';
const Video = dynamic(() => import('~/shared/components/Video/Video'));

type M12Hero5050ModuleProps = M12Hero5050Module;

const M12Hero5050ModuleComponent = ({
    trumpet,
    title,
    paragraphText,
    image,
    imageAltText,
    imageOnBackground,
    colorTheme,
    watermark,
    link,
    disclaimer,
    titleSize,
    linkType,
    video,
}: M12Hero5050ModuleProps) => {
    const variants = useMemo(() => {
        let titleVariant: TypographyStyle = 'title-L';
        let linkVariant: ButtonColorProps = 'primary';

        if (titleSize === 'Large') titleVariant = 'display-M';
        else if (titleSize === 'Extra Large') titleVariant = 'display-XL';

        if (linkType === 'Secondary') linkVariant = 'new-secondary';
        else if (linkType === 'Text') linkVariant = 'new-link';

        return {
            title: titleVariant,
            link: linkVariant,
        };
    }, []);

    return (
        <section
            className={styles.component}
            style={
                {
                    '--shadow-color': colorTheme.highlightColor,
                    '--background-color': colorTheme.mainColor,
                    '--watermark-color': colorTheme.secondaryColor,
                    '--watermark-opacity': colorTheme.secondaryColorOpacity,
                    '--bullet-color':
                        colorTheme.mainColor === '#f68a20' ? colorTheme.foregroundColor : '#f68a20',
                    color: colorTheme.foregroundColor,
                } as React.CSSProperties
            }
        >
            <ModuleContainer hasGutter>
                <div className={styles.wrapper}>
                    <div className={styles.bg}>
                        {!!watermark &&
                            !imageOnBackground &&
                            (watermark === 'Logo' ? (
                                <WaterMarkLogo className={styles.markLogo} />
                            ) : (
                                <WaterMarkPercent className={styles.markPercentage} />
                            ))}
                    </div>
                    <div className={styles.textContainer}>
                        <div className={styles.textInner}>
                            {trumpet && (
                                <Typography as="p" variant="trumpet-XL" className={styles.trumpet}>
                                    {trumpet}
                                </Typography>
                            )}

                            {title && (
                                <Typography variant={variants.title} as="h2">
                                    {title}
                                </Typography>
                            )}

                            {paragraphText && (
                                <Typography
                                    className={styles.rte}
                                    variant="paragraph-L"
                                    as="div"
                                    dangerouslySetInnerHTML={{ __html: paragraphText }}
                                />
                            )}

                            {link?.url && (
                                <Button
                                    className={styles.button}
                                    as={Link}
                                    aria-label="link"
                                    variant={variants.link}
                                    href={link.url}
                                    target={link.target}
                                    isInverted={colorTheme.foregroundColor === '#ffffff'}
                                >
                                    {link.title}
                                </Button>
                            )}
                        </div>
                    </div>

                    <figure
                        className={clsx(styles.imageContainer, {
                            [styles.imageOnBackground]: imageOnBackground,
                        })}
                    >
                        {watermark &&
                            imageOnBackground &&
                            (watermark === 'Logo' ? (
                                <WaterMarkLogo className={styles.markLogo} />
                            ) : (
                                <WaterMarkPercent className={styles.markPercentage} />
                            ))}

                        {video ? (
                            <Video
                                name={title}
                                muted={video.mute}
                                controls={false}
                                src={video.src}
                                loop={video.loop}
                                playing={video.autoPlay}
                                posterSrc={image?.src}
                                cover={true}
                                theaterMode={video.theaterMode}
                            />
                        ) : (
                            <Image
                                className={styles.image}
                                src={image?.src ?? ''}
                                width={520}
                                height={390}
                                alt={imageAltText ?? ''}
                                isNotLegacy
                            />
                        )}
                    </figure>
                </div>

                {disclaimer && (
                    <Typography
                        variant="disclaimer-M"
                        as="div"
                        className={styles.disclaimer}
                        dangerouslySetInnerHTML={{ __html: disclaimer }}
                    />
                )}
            </ModuleContainer>
        </section>
    );
};

export default M12Hero5050ModuleComponent;
